import { AtlBrowserStorageSession } from '@atlassian/browser-storage-controls';

/**
 * Browser-session-storage-backed store tracking which nudges have already been shown.
 * Keyed per cloudId. Entries expire after 7 days; capped at 500 to prevent unbounded growth.
 */

import { isBrowser } from '../is-browser';

const STORAGE_KEY_PREFIX = 'rovo-button-nudge-suppression-store';
const SCHEMA_VERSION = 1;

// Bounded growth constraints.
const MAX_ENTRIES = 500;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Map of suppression key → expiresAt (epoch ms). */
export type SuppressionMap = Map<string, number>;

export type StorageScope = {
	cloudId: string;
};

/** In-memory cache keyed by storage key. Prevents JSON.parse on every render. */
const memoryCache = new Map<string, SuppressionMap>();

const buildStorageKey = ({ cloudId }: StorageScope): string =>
	`${STORAGE_KEY_PREFIX}:v${SCHEMA_VERSION}:${cloudId}`;

/**
 * Drop expired entries and, if still over the cap, drop the oldest by expiry.
 * Returns a new map (pure).
 */
const pruneEntries = (map: SuppressionMap, now: number = Date.now()): SuppressionMap => {
	// 1. Drop expired entries.
	const fresh: SuppressionMap = new Map();
	for (const [key, expiresAt] of map) {
		if (expiresAt > now) {
			fresh.set(key, expiresAt);
		}
	}
	// 2. If still over cap, drop oldest entries by expiry.
	if (fresh.size <= MAX_ENTRIES) {
		return fresh;
	}
	const sorted = Array.from(fresh.entries()).sort((a, b) => a[1] - b[1]);
	return new Map(sorted.slice(sorted.length - MAX_ENTRIES));
};

/** Best-effort write of a suppression map to sessionStorage. Memory cache is always source of truth. */
const writeToStorage = (storageKey: string, map: SuppressionMap): void => {
	try {
		const value = JSON.stringify(Array.from(map.entries()));
		AtlBrowserStorageSession.setItem(storageKey, value);
	} catch {
		// Best-effort. Memory cache is always source of truth.
	}
};

/** Parse the persisted JSON for the given storage key. Returns an empty Map on miss/corrupt. */
const parseFromStorage = (storageKey: string): SuppressionMap => {
	try {
		const raw = AtlBrowserStorageSession.getItem(storageKey);
		if (!raw) {
			return new Map();
		}
		const arr: unknown = JSON.parse(raw);
		if (
			Array.isArray(arr) &&
			arr.every(
				(e): e is [string, number] =>
					Array.isArray(e) && typeof e[0] === 'string' && typeof e[1] === 'number',
			)
		) {
			return new Map(arr);
		}
	} catch {
		// Corrupt JSON or schema mismatch — start fresh.
	}
	return new Map();
};

/**
 * Read the suppression map for the given scope.
 * - SSR-safe: returns empty Map on the server.
 * - Memo-cached: only parses sessionStorage on first call per scope.
 * - Read-only: never writes back to sessionStorage. Writes happen only in writeSuppression.
 */
export const readSuppressionMap = (scope: StorageScope): SuppressionMap => {
	if (!isBrowser()) {
		return new Map();
	}
	const storageKey = buildStorageKey(scope);
	const cached = memoryCache.get(storageKey);
	if (cached) {
		// Defensive copy so callers cannot mutate the cache.
		return new Map(cached);
	}
	const parsed = parseFromStorage(storageKey);
	memoryCache.set(storageKey, parsed);
	return new Map(parsed);
};

/**
* Records a suppression entry for the given key. Prunes expired/excess entries on every write.
* Note: sessionStorage is per-tab — a user with multiple tabs open may see the same nudge once per tab.
*/
export const writeSuppression = (
	scope: StorageScope,
	suppressionKey: string,
): void => {
	if (!isBrowser()) {
		return;
	}
	const storageKey = buildStorageKey(scope);
	const current = readSuppressionMap(scope);
	// Guard against silently refreshing TTL on an already-suppressed key.
	if (current.has(suppressionKey)) {
		return;
	}
	const next = new Map(current);
	next.set(suppressionKey, Date.now() + MAX_AGE_MS);
	const pruned = pruneEntries(next);
	memoryCache.set(storageKey, pruned);
	writeToStorage(storageKey, pruned);
};

/** Returns true if the suppression key has an active, non-expired entry. */
export const isKeySuppressed = (scope: StorageScope, suppressionKey: string): boolean => {
	const expiresAt = readSuppressionMap(scope).get(suppressionKey);
	return expiresAt !== undefined && expiresAt > Date.now();
};

/** Remove a single suppression entry. Used by the debug API. */
export const clearSuppression = (scope: StorageScope, suppressionKey: string): void => {
	if (!isBrowser()) {
		return;
	}
	const storageKey = buildStorageKey(scope);
	const next = new Map(readSuppressionMap(scope));
	next.delete(suppressionKey);
	memoryCache.set(storageKey, next);
	writeToStorage(storageKey, next);
};

/** Remove all suppression entries for the scope. Used by the debug API. */
export const clearAllSuppression = (scope: StorageScope): void => {
	if (!isBrowser()) {
		return;
	}
	const storageKey = buildStorageKey(scope);
	memoryCache.delete(storageKey);
	try {
		AtlBrowserStorageSession.removeItem(storageKey);
	} catch {
		// Best-effort.
	}
};

